/**
 * The base planner (the owner, Oct 20: "with all the different buildings and systems we're now getting map layouts that can
 * have buildings partially inside eachother or overlapping, we probably want to have some way to autoplan bases better").
 * The diagnosis (BUILDLIST, the base planner's row): the sim's footprints never overlapped — settleBuildings keeps their
 * discs apart — but a seated building's **stand** (core/stands.ts: its deck, and the skirt it grows down a slope, rise ×
 * STAND.rampRun) is what is drawn, and on a slope the skirts reached into the neighbours (the HQ's into the pad's up to 40
 * people on a steep base). So, one pass after settleBuildings and before the terraces and the seat, on the action clock with
 * the buildings sized by function, over the base's structures in a fixed order of priority (BASE_PLAN.order: the HQ, the
 * pad, the point defence, the stores, the installations, then the houses), each claiming its **reach** — its deck and the
 * skirt it would grow where it stands (its seat and stand there, 67.52 and 209's footing), or its hitbox's rectangle where
 * it has no deck — widened by BUILDING_CLEARANCE, against everything claimed before it (settleBuildings has already kept
 * the discs off the points, the spawns, the parked vehicles and each other):
 * - clear, it stays;
 * - its skirt into a neighbour's reach with the terraces on (the coordinator, for the owner's compact bases, 205: "flat
 *   ground first, terraces where needed"): **one shared terrace** laid under both decks at their median level (core/terraces.ts'
 *   record, through the log) when the ground under both rises no more than TERRACE.maxStepPeople — then neither grows a
 *   skirt toward the other — and it stays if that clears it;
 * - else it takes the clear spot on the nearest ring round where it was put (BASE_PLAN.ringPeople apart, out to
 *   BASE_PLAN.maxPeople, sixteen a ring) whose reach is least (the flattest) — clear of the claims, of the other structures
 *   standing (by their own reach) and of the capture points' circles;
 * - a settlement's house that does not clear is left out (its lot empty; the settlement's lane stays straight).
 * Pure and deterministic (the structures' order, keyed rings); the turn game's placement and a classic-scale match are as
 * they were.
 */
import type { GameState, PlanetState, StructureState, StructureType, Vec3, TerrainEdit } from '../types';
import type { Terrace } from '../types/ground';
import { clockPolicyFor } from '../match/clockPolicy';
import { structureHitbox, structureFootprint, BUILDING_CLEARANCE } from './buildingScale';
import { footingOf, standOf, withStands } from './stands';
import { seatBuild } from './buildSeat';
import { footprintGround, terraceEdit, terracesOn } from './terraces';
import { TERRACE } from './terraceGround';
import { LIGHTER } from '../constants/action';
import { applyTerrainEdit } from './terrainEdits';
import { structureFrame } from './hits';
import { gateLanes } from './gateLanes';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dcos, dsin, dacos } from './dmath';
import { vecAdd, vecCross, vecDot, vecNormalize, vecScale, vecSub, surfaceTangentFrame } from '../math';

export const BASE_PLAN = {
  /** The order the base's structures claim their ground in (earlier first; the rest after, in the structures' order). */
  order: ['ecotourism', 'landingPad', 'pointDefence', 'generator', 'towedGun', 'helipad', 'supply', 'shield', 'antenna', 'house'] as readonly StructureType[],   // (69.13 (a): the towed guns after the mounts)   // (not the harness's towers and gun emplacements: stood by a unit, not a base)
  /** Rings searched for a clear spot: this far apart (people), out to `maxPeople`. */
  ringPeople: 2, maxPeople: 40, perRing: 16,
};

/** A reach on the ground: a centre (unit direction), its axes and half extents (world). */
interface Reach { c: Vec3; tx: Vec3; tz: Vec3; hx: number; hz: number }

/** How deep two reaches overlap on their tightest separating axis (world; ≤ 0: apart), on the tangent plane at the first. */
function overlap(a: Reach, b: Reach, R: number): number {
  const off = vecScale(vecSub(b.c, a.c), R);
  let least = Infinity;
  for (const ax of [a.tx, a.tz, b.tx, b.tz]) {
    const ra = a.hx * Math.abs(vecDot(a.tx, ax)) + a.hz * Math.abs(vecDot(a.tz, ax)), rb = b.hx * Math.abs(vecDot(b.tx, ax)) + b.hz * Math.abs(vecDot(b.tz, ax));
    least = Math.min(least, ra + rb - Math.abs(vecDot(off, ax)));
  }
  return least;
}

/** Which structures the planner lays: the seated builds with a deck (a building's plinth, the pad), the point defence, the stores and the houses. */
const planned = (s: StructureState): boolean => BASE_PLAN.order.includes(s.type);

/** A structure's hitbox as a rectangle in its frame (world): its parts' extents across x and z by its ruler. */
function hitboxReach(state: Pick<GameState, 'config'>, s: StructureState, R: number): { hx: number; hz: number } {
  const def = structureHitbox(s, state.config);
  let hx = 0, hz = 0;
  for (const q of def.parts) {
    if (q.shape === 'box') { hx = Math.max(hx, Math.abs(q.centre[0]) + q.half[0]); hz = Math.max(hz, Math.abs(q.centre[2]) + q.half[2]); }
    else { hx = Math.max(hx, Math.abs(q.a[0]) + q.radius, Math.abs(q.b[0]) + q.radius); hz = Math.max(hz, Math.abs(q.a[2]) + q.radius, Math.abs(q.b[2]) + q.radius); }
  }
  const half = Math.max(hx, hz), scale = half > 0 ? structureFootprint(s, state.config, R) / half : 0;
  return { hx: hx * scale, hz: hz * scale };
}

/** A structure's reach where it stands: its stand's deck and skirt there (seated as the seat would seat it), else its hitbox's rectangle; widened by `margin`. */
function reachOf(state: GameState, s: StructureState, margin: number): Reach {
  const planet = state.planets[s.planetIndex], up = vecNormalize(s.surfaceDir);
  const { tx, tz } = s.drive?.heading ? (() => { const f = vecNormalize(s.drive!.heading); return { tz: f, tx: vecNormalize(vecCross(up, f)) }; })() : structureFrame(s, up);
  if (footingOf(s, state.config)) {
    const stand = standOf(state, seatBuild(state, s));
    if (stand) return { c: up, tx: stand.tx, tz: stand.tz, hx: stand.halfX + stand.skirt + margin, hz: stand.halfZ + stand.skirt + margin };
  }
  const { hx, hz } = hitboxReach(state, s, planet.radius);
  return { c: up, tx, tz, hx: hx + margin, hz: hz + margin };
}

/** The direction `dist` world units from `n` along the tangent at angle `ang` of its frame. */
function ringSpot(n: Vec3, ang: number, dist: number, R: number): Vec3 {
  const { tx, ty } = surfaceTangentFrame(n), a = dist / R;
  return vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(vecAdd(vecScale(tx, dcos(ang)), vecScale(ty, dsin(ang))), dsin(a))));
}

/**
 * One shared terrace under two neighbours (their union's rectangle in the first's frame — a deck, or a deckless build's
 * hitbox — at the median of the ground under it), laid when the ground under it rises no more than TERRACE.maxStepPeople;
 * null when it would not (the step past the cap). The first must have a deck (its skirt is what the terrace takes away).
 */
function sharedTerrace(state: GameState, a: StructureState, b: StructureState, nextId: number): { planet: PlanetState; edit: TerrainEdit } | null {
  const planet = state.planets[a.planetIndex];
  if (!planet || !footingOf(a, state.config) || planet.type !== 'planet' || !planet.alive) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  // a side's half extents (world): its deck's, else its hitbox's
  const halves = (s: StructureState) => { const f = footingOf(s, state.config); if (f) return { hx: f.halfXPeople * person, hz: f.halfZPeople * person }; return hitboxReach(state, s, R); };
  const ua = vecNormalize(a.surfaceDir), { tz } = structureFrame(a, ua), across = vecNormalize(vecCross(tz, ua));
  // each deck's corners in the first's frame (world): the union's box
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const s of [a, b]) {
    const u = vecNormalize(s.surfaceDir), fr = structureFrame(s, u), { hx, hz } = halves(s);
    const at = vecScale(vecSub(u, ua), R);
    const sx = vecNormalize(vecCross(fr.tz, u));
    for (const [i, j] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const p = vecAdd(at, vecAdd(vecScale(sx, i * hx), vecScale(fr.tz, j * hz)));
      const x = vecDot(p, across), z = vecDot(p, tz);
      x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
    }
  }
  const halfX = (x1 - x0) / 2, halfZ = (z1 - z0) / 2;
  const centre = vecNormalize(vecAdd(ua, vecScale(vecAdd(vecScale(across, (x0 + x1) / 2), vecScale(tz, (z0 + z1) / 2)), 1 / R)));
  const { rise, level } = footprintGround(planet, centre, tz, across, halfX, halfZ, ps);
  if (rise > TERRACE.maxStepPeople * person) return null;
  const t: Terrace = { id: nextId, dir: centre, facing: tz, halfX, halfZ, band: TERRACE.bandPeople * person, level };
  const edit = terraceEdit(a.planetIndex, planet, t);
  return { planet: applyTerrainEdit(planet, edit), edit };
}

/** The setup's pass (match/setup.ts, after settleBuildings): the base's structures laid clear of each other's reach. The same state when nothing moves. */
export function planBases(state: GameState): GameState {
  if (clockPolicyFor(state.config).buildings !== 'function' || !state.structures.some(planned)) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, margin = (BUILDING_CLEARANCE * person) / 2;
  let st: GameState = state;
  let nextTerrace = 1;
  for (const p of state.planets) for (const t of p.terraces ?? []) nextTerrace = Math.max(nextTerrace, t.id + 1);
  const rank = (s: StructureState) => { const i = BASE_PLAN.order.indexOf(s.type); return i < 0 ? BASE_PLAN.order.length : i; };
  const queue = state.structures.filter(s => s.health > 0 && planned(s)).sort((a, b) => rank(a) - rank(b) || a.id - b.id).map(s => s.id);
  const claimed: Array<{ id: number; planetIndex: number; reach: Reach }> = [];
  const others = (id: number) => st.structures.filter(o => o.health > 0 && o.id !== id && !queue.includes(o.id));
  const dropped = new Set<number>();
  for (const id of queue) {
    const s0 = st.structures.find(x => x.id === id)!, planet = st.planets[s0.planetIndex];
    if (!planet) continue;
    const R = planet.radius;
    // a move is the claims' to ask (a higher claim's reach); a new spot also clears the other structures standing (their own
    // reach) and the points' circles
    const fixed = others(id).filter(o => o.planetIndex === s0.planetIndex).map(o => reachOf(st, o, margin));
    const circles: Array<[Vec3, number]> = (st.modeState.points?.sites ?? []).filter(x => x.planetIndex === s0.planetIndex).map(x => [x.surfaceDir, ps.captureRadius]);
    const hits = (r: Reach) => {
      const out: number[] = [];
      claimed.forEach((c, k) => { if (c.planetIndex === s0.planetIndex && overlap(r, c.reach, R) > 0) out.push(k); });
      return out;
    };
    const blocked = (r: Reach) => fixed.some(f => overlap(r, f, R) > 0)
      || circles.some(([at, rad]) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(at), r.c)))) * R < rad + Math.min(r.hx, r.hz));
    const clearAt = (s: StructureState) => { const r = reachOf(st, s, margin); return { r, clear: hits(r).length === 0 }; };
    const freeAt = (s: StructureState) => { const t = clearAt(s); return { r: t.r, clear: t.clear && !blocked(t.r) }; };
    let here = clearAt(s0);
    // the cheaper fix first (terraces on): one shared terrace under it and the first neighbour its reach meets
    if (!here.clear && terracesOn(st)) {
      const k = hits(here.r)[0], nb = k !== undefined ? st.structures.find(x => x.id === claimed[k].id) : undefined;
      // (the one with a deck first: its skirt is what the terrace takes away; neither with one, nothing to level)
      const laid = !nb ? null : footingOf(nb, st.config) ? sharedTerrace(st, nb, s0, nextTerrace) : footingOf(s0, st.config) ? sharedTerrace(st, s0, nb, nextTerrace) : null;
      if (laid) {
        const trial: GameState = { ...st, planets: st.planets.map((p, i) => (i === s0.planetIndex ? laid.planet : p)), terrainEdits: [...(st.terrainEdits ?? []), laid.edit] };
        const before = st;
        st = trial;
        // the neighbour's reach again, on the terrace
        const kk = k!;
        const again = { ...claimed[kk], reach: reachOf(st, nb!, margin) };
        claimed[kk] = again;
        here = clearAt(s0);
        if (here.clear) nextTerrace++;
        else { st = before; claimed[kk] = { ...again, reach: reachOf(st, nb!, margin) }; here = clearAt(s0); }
      }
    }
    if (here.clear) { claimed.push({ id, planetIndex: s0.planetIndex, reach: here.r }); continue; }
    if (s0.type === 'house') { dropped.add(id); continue; }   // (a settlement's lot left empty: its lane stays straight)
    // the nearest ring with a clear spot; on it, the least reach (the flattest)
    const n = vecNormalize(s0.surfaceDir);
    let best: { s: StructureState; r: Reach; size: number } | null = null;
    for (let ring = 1; ring * BASE_PLAN.ringPeople <= BASE_PLAN.maxPeople && !best; ring++) {
      for (let k = 0; k < BASE_PLAN.perRing; k++) {
        const s = { ...s0, surfaceDir: ringSpot(n, (k / BASE_PLAN.perRing) * 2 * Math.PI, ring * BASE_PLAN.ringPeople * person, R) };
        const t = freeAt(s);
        if (!t.clear) continue;
        const size = t.r.hx * t.r.hz;
        if (!best || size < best.size) best = { s, r: t.r, size };
      }
    }
    if (best) {
      st = { ...st, structures: st.structures.map(x => (x.id === id ? best!.s : x)) };
      claimed.push({ id, planetIndex: s0.planetIndex, reach: best.r });
    } else claimed.push({ id, planetIndex: s0.planetIndex, reach: here.r });   // (nowhere clear within reach: it stays where it was put)
  }
  if (dropped.size) st = { ...st, structures: st.structures.filter(x => !dropped.has(x.id)) };
  return st;
}

/**
 * 63.16 (h) (the owner: "Sure 3, 4, 6" on lane F's known-wrong list; the coordinator: "the base planner claims the lighter strip
 * and the motor pool, in its order, so nothing stands under the strip's deck or the pool's compound"): the yards' pass, after
 * the pools and the strips are sited (match/setup.ts: they are sited past the base the first pass laid, so the first pass
 * could not claim them). Each side's motor pool compound (its stand: core/motorPoolStock.ts) and lighter strip (its deck's
 * footprint and the terrace band round it, core/lighterStrip.ts) claim their ground first; every base structure but the HQ
 * and the pad (the base's own, the pool sited past them) and every settlement house whose reach meets a claim moves to the nearest clear ring round where it stood — clear of the yards,
 * of the other structures standing and of the points' circles, the least reach on the ring — and is seated again where it
 * now stands; a house that finds no spot is dropped, as in the first pass; the stands are laid again. Nothing: the state as
 * it was (no pools, no strips, or nothing under them — bit-identical).
 */
export function planYards(state: GameState): GameState {
  if (clockPolicyFor(state.config).buildings !== 'function') return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, margin = (BUILDING_CLEARANCE * person) / 2;
  const yards: Array<{ planetIndex: number; reach: Reach }> = [];
  // (the compound's deck, not its skirt: a 34 × 30 compound on a hillside grows a skirt of a dozen people that would reach the base
  // itself — the HQ and the pad stay where the first pass put them, the pool was sited past them; a build on the skirt's slope is
  // the lesser jank, the compound's own terrace the fix for it)
  for (const pool of state.structures) if (pool.type === 'motorPool' && pool.health > 0) {
    const stand = standOf(state, pool);
    yards.push({ planetIndex: pool.planetIndex, reach: stand ? { c: stand.dir, tx: stand.tx, tz: stand.tz, hx: stand.halfX + margin, hz: stand.halfZ + margin } : reachOf(state, pool, margin) });
  }
  // (GATE_LANE: each pool's gate lanes too — a towed gun stood ~35 people out of s71-5's and s71-8's gates, in the way of what
  // the pool unpacks; core/gateLanes.ts)
  for (const l of gateLanes(state)) yards.push({ planetIndex: l.planetIndex, reach: { c: l.c, tx: l.tx, tz: l.tz, hx: l.hx + margin, hz: l.hz } });
  for (const strip of Object.values(state.lighterStrips ?? {})) {
    const up = vecNormalize(strip.dir), tz = vecNormalize(strip.front), tx = vecNormalize(vecCross(tz, up));
    const band = TERRACE.bandPeople * person;
    yards.push({ planetIndex: strip.planetIndex, reach: { c: up, tx, tz, hx: (LIGHTER.strip.acrossPeople / 2) * person + band + margin, hz: (LIGHTER.strip.alongPeople / 2) * person + band + margin } });
  }
  if (!yards.length) return state;
  let st: GameState = state;
  const rank = (s: StructureState) => { const i = BASE_PLAN.order.indexOf(s.type); return i < 0 ? BASE_PLAN.order.length : i; };
  const anchors: readonly StructureType[] = ['ecotourism', 'landingPad'];   // (the base's own: never moved for a yard)
  const queue = state.structures.filter(s => s.health > 0 && planned(s) && s.type !== 'motorPool' && !anchors.includes(s.type)).sort((a, b) => rank(a) - rank(b) || a.id - b.id).map(s => s.id);
  const dropped = new Set<number>();
  let moved = false;
  for (const id of queue) {
    const s0 = st.structures.find(x => x.id === id)!, planet = st.planets[s0.planetIndex];
    if (!planet) continue;
    const R = planet.radius;
    const mine = yards.filter(y => y.planetIndex === s0.planetIndex);
    if (!mine.length) continue;
    const under = (r: Reach) => mine.some(y => overlap(r, y.reach, R) > 0);
    const here = reachOf(st, s0, margin);
    if (!under(here)) continue;
    if (s0.type === 'house') { dropped.add(id); continue; }
    const fixed = st.structures.filter(o => o.health > 0 && o.id !== id && o.planetIndex === s0.planetIndex && !dropped.has(o.id)).map(o => reachOf(st, o, margin));
    const circles: Array<[Vec3, number]> = (st.modeState.points?.sites ?? []).filter(x => x.planetIndex === s0.planetIndex).map(x => [x.surfaceDir, ps.captureRadius]);
    const blocked = (r: Reach) => under(r) || fixed.some(f => overlap(r, f, R) > 0)
      || circles.some(([at, rad]) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(at), r.c)))) * R < rad + Math.min(r.hx, r.hz));
    const n = vecNormalize(s0.surfaceDir);
    let best: { s: StructureState; size: number } | null = null;
    for (let ring = 1; ring * BASE_PLAN.ringPeople <= BASE_PLAN.maxPeople && !best; ring++) {
      for (let k = 0; k < BASE_PLAN.perRing; k++) {
        const { baseLift: _l, ...bare } = s0;
        const s = { ...bare, surfaceDir: ringSpot(n, (k / BASE_PLAN.perRing) * 2 * Math.PI, ring * BASE_PLAN.ringPeople * person, R) };
        const r = reachOf(st, s, margin);
        if (blocked(r)) continue;
        const size = r.hx * r.hz;
        if (!best || size < best.size) best = { s, size };
      }
    }
    if (!best) continue;   // (nowhere clear within reach: it stays)
    const seated = seatBuild(st, best.s);
    st = { ...st, structures: st.structures.map(x => (x.id === id ? seated : x)) };
    moved = true;
  }
  if (dropped.size) { st = { ...st, structures: st.structures.filter(x => !dropped.has(x.id)) }; moved = true; }
  return moved ? withStands(st) : state;
}
